"use client";

import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import Select from "react-select";
import * as XLSX from "xlsx";
import {
  fetchPriceUpdateLogs,
  downloadPriceUpdateXlsx,
  downloadPriceUpdateCsv,
} from "./priceUpdateExport";
import {
  XMarkIcon,
  ArrowUpTrayIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  ArrowRightIcon,
} from "@heroicons/react/20/solid";
import {
  parseVendorSheet,
  compareVendorPrices,
  normSku,
  suggestCandidates,
  buildSuggestionRow,
  isAutoSelectable,
  AUTO_SELECT_CONFIDENCE,
} from "./vendorPriceUtils";

const API_BASE = "https://erp-production-72da01c8e651.herokuapp.com/api";
const ROW_LIMIT = 300;
const AI_BATCH = 10;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const cls = (...c) => c.filter(Boolean).join(" ");

const fmtPrice = (n) =>
  n === null || n === undefined || Number.isNaN(n)
    ? "—"
    : `$${Number(n).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      })}`;

const fmtDiff = (d) =>
  `${d > 0 ? "+" : d < 0 ? "-" : ""}${fmtPrice(Math.abs(d))}`;

const fmtPct = (p) =>
  p === null || p === undefined ? "—" : `${p > 0 ? "+" : ""}${p.toFixed(2)}%`;

// ─── react-select dark theme (matches ItemDatabaseManager) ───────────────────
const selectStyles = {
  control: (p, s) => ({
    ...p,
    backgroundColor: "#111827",
    borderColor: s.isFocused ? "#f97316" : "#374151",
    boxShadow: s.isFocused ? "0 0 0 1px #f97316" : "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "40px",
    fontSize: "13px",
    borderRadius: "0.5rem",
  }),
  input: (p) => ({ ...p, color: "#e5e7eb" }),
  singleValue: (p) => ({ ...p, color: "#e5e7eb" }),
  placeholder: (p) => ({ ...p, color: "#6b7280" }),
  option: (p, s) => ({
    ...p,
    backgroundColor: s.isFocused ? "#1f2937" : "#111827",
    color: s.isSelected ? "#f97316" : "#e5e7eb",
    fontSize: "13px",
    cursor: "pointer",
  }),
  menu: (p) => ({
    ...p,
    backgroundColor: "#111827",
    border: "1px solid #1f2937",
    borderRadius: "0.5rem",
  }),
  menuPortal: (p) => ({ ...p, zIndex: 100000 }),
};

const APPLY_CHUNK = 300; // rows per request (keeps bodies small; one transaction each)

const EMPTY_TOTALS = {
  updated: 0,
  unchanged: 0,
  skuChanged: 0,
  vendorEntriesAdded: 0,
  errors: [],
};

const CONF_STEPS = [
  { label: "Any", value: 0 },
  { label: "≥ 70%", value: 70 },
  { label: "≥ 85%", value: 85 },
  { label: "≥ 90%", value: 90 },
  { label: "≥ 95%", value: 95 },
  { label: "100%", value: 100 },
];

const DIR_STEPS = [
  { label: "All", value: "all" },
  { label: "Increases", value: "up" },
  { label: "Decreases", value: "down" },
  { label: "Big (≥ 25%)", value: "big" },
];

// noun used on the Apply button for each tab
const APPLY_NOUN = {
  changes: ["price change", "price changes"],
  suggested: ["suggested match", "suggested matches"],
  review: ["reviewed item", "reviewed items"],
};

const TABS = [
  { id: "changes", label: "Price changes" },
  { id: "suggested", label: "Suggested matches" },
  { id: "review", label: "Needs review" },
  { id: "same", label: "No change" },
  { id: "notInDb", label: "Not in our database" },
  { id: "notInFile", label: "Not in vendor file" },
];

export default function VendorPriceUpdate({
  vendors = [],
  onUpdated,
  onClose,
}) {
  const [vendor, setVendor] = useState(null);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [phase, setPhase] = useState("setup"); // setup | review | done
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState("");
  const [parseInfo, setParseInfo] = useState(null);
  const [result, setResult] = useState(null); // compareVendorPrices() output
  const [selected, setSelected] = useState(() => new Set());
  const [tab, setTab] = useState("changes");
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [suggestions, setSuggestions] = useState([]); // description matches
  const [applied, setApplied] = useState(() => new Set()); // keys already saved
  const [totals, setTotals] = useState(EMPTY_TOTALS);
  const [lastApply, setLastApply] = useState(null);
  const [confMin, setConfMin] = useState(0); // suggested tab: min confidence
  const [safeOnly, setSafeOnly] = useState(false); // suggested tab: specs+units agree
  const [dir, setDir] = useState("all"); // all | up | down | big
  const [ai, setAi] = useState({
    running: false,
    done: 0,
    total: 0,
    ran: false,
    warning: "",
  });
  const [logIds, setLogIds] = useState([]); // audit-log docs saved this session
  const [exporting, setExporting] = useState(false);
  const sessionIdRef = useRef(
    `pu-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
  );
  const catalogRef = useRef([]);
  const fileInputRef = useRef(null);

  const vendorOptions = useMemo(
    () => vendors.map((v) => ({ value: v._id, label: v.companyName })),
    [vendors],
  );

  // Esc closes (unless a write is in flight)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !applying) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, applying]);

  const pickFile = useCallback((f) => {
    if (!f) return;
    if (!/\.(xlsx|xls|csv)$/i.test(f.name)) {
      setError("Please choose an Excel (.xlsx / .xls) or .csv file");
      return;
    }
    setError("");
    setFile(f);
  }, []);

  // ─── Parse + compare ───────────────────────────────────────────────────────
  const runCompare = async () => {
    if (!vendor || !file) return;
    setError("");
    setBusy(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });

      const bySku = new Map(); // dedupe across sheets, last one wins
      let variantRowsIgnored = 0;
      let duplicateSkus = 0;
      for (const name of wb.SheetNames) {
        const rows = XLSX.utils.sheet_to_json(wb.Sheets[name], {
          header: 1,
          raw: true,
          defval: null,
          blankrows: false,
        });
        const parsed = parseVendorSheet(rows, { sheetName: name });
        variantRowsIgnored += parsed.variantRowsIgnored;
        duplicateSkus += parsed.duplicates.length;
        for (const it of parsed.items) {
          const k = normSku(it.sku);
          if (bySku.has(k)) duplicateSkus++;
          bySku.set(k, it);
        }
      }
      const parsedItems = [...bySku.values()];
      if (!parsedItems.length) {
        throw new Error(
          "No item numbers with a unit price were found in that file.",
        );
      }

      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${API_BASE}/item-database/catalog?category=M&isActive=true`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const cmp = compareVendorPrices({
        parsedItems,
        catalog: res.data.items || [],
        vendorId: vendor.value,
      });

      catalogRef.current = res.data.items || [];
      setSuggestions([]);
      setApplied(new Set());
      setTotals(EMPTY_TOTALS);
      setLastApply(null);
      setConfMin(0);
      setSafeOnly(false);
      setDir("all");
      setAi({ running: false, done: 0, total: 0, ran: false, warning: "" });
      setParseInfo({ variantRowsIgnored, duplicateSkus });
      setResult(cmp);
      setSelected(
        new Set(
          cmp.rows
            .filter(
              (r) =>
                (r.status === "increase" || r.status === "decrease") &&
                !r.converted,
            )
            .map((r) => r.key),
        ),
      );
      setTab(
        cmp.summary.increase + cmp.summary.decrease > 0 ? "changes" : "same",
      );
      setSearch("");
      setShowAll(false);
      setPhase("review");
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  // ─── Derived rows per tab ──────────────────────────────────────────────────
  // Rows/suggestions that were already saved drop out of every list
  const liveRows = useMemo(
    () => (result ? result.rows.filter((r) => !applied.has(r.key)) : []),
    [result, applied],
  );
  const liveSuggestions = useMemo(
    () => suggestions.filter((s) => !applied.has(s.key)),
    [suggestions, applied],
  );

  const tabData = useMemo(() => {
    if (!result) return {};
    // a vendor line that has a suggestion (applied or not) is no longer "unmatched"
    const suggestedSkus = new Set(suggestions.map((s) => normSku(s.sku)));
    const q = search.trim().toLowerCase();
    const hit = (...vals) =>
      !q ||
      vals.some((v) =>
        String(v ?? "")
          .toLowerCase()
          .includes(q),
      );

    const matchedFilter = (r) =>
      hit(r.itemCode, r.description, r.sku, r.vendorDescription);

    const dirOk = (r) =>
      dir === "all" ||
      (dir === "up" && r.diff > 0) ||
      (dir === "down" && r.diff < 0) ||
      (dir === "big" && r.bigChange);

    const suggestionOk = (s) =>
      s.confidence >= confMin &&
      (!safeOnly || (s.specOk && s.unitOk)) &&
      dirOk(s);

    return {
      changes: liveRows.filter(
        (r) =>
          (r.status === "increase" || r.status === "decrease") &&
          dirOk(r) &&
          matchedFilter(r),
      ),
      review: liveRows.filter(
        (r) =>
          (r.status === "mismatch" || r.status === "invalid") &&
          matchedFilter(r),
      ),
      same: liveRows.filter((r) => r.status === "same" && matchedFilter(r)),
      suggested: liveSuggestions.filter(
        (s) =>
          suggestionOk(s) &&
          hit(s.itemCode, s.description, s.sku, s.vendorDescription),
      ),
      notInDb: result.unmatchedVendor.filter(
        (v) => !suggestedSkus.has(normSku(v.sku)) && hit(v.sku, v.description),
      ),
      notInFile: result.missingFromFile.filter((m) =>
        hit(m.itemCode, m.description, m.sku),
      ),
    };
  }, [
    result,
    search,
    suggestions,
    liveRows,
    liveSuggestions,
    dir,
    confMin,
    safeOnly,
  ]);

  // Tab badges (not affected by search / filters)
  const tabCounts = useMemo(() => {
    if (!result) return {};
    const n = (st) => liveRows.filter((r) => r.status === st).length;
    return {
      changes: n("increase") + n("decrease"),
      up: n("increase"),
      down: n("decrease"),
      suggested: liveSuggestions.length,
      review: n("mismatch") + n("invalid"),
      same: n("same"),
      notInDb: result.summary.unmatchedVendor - suggestions.length,
      notInFile: result.summary.missingFromFile,
    };
  }, [result, liveRows, liveSuggestions, suggestions]);

  const selectableTab =
    tab === "changes" || tab === "review" || tab === "suggested";
  const visible = tabData[tab] || [];
  const shown = showAll ? visible : visible.slice(0, ROW_LIMIT);

  const toggle = (key) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
        // one description match per item: picking this unticks any other
        // vendor line that was also matched to the same item
        const s = suggestions.find((x) => x.key === key);
        if (s)
          suggestions.forEach((o) => {
            if (o.key !== key && String(o.itemId) === String(s.itemId))
              next.delete(o.key);
          });
      }
      return next;
    });

  const allVisibleSelected =
    selectableTab &&
    visible.length > 0 &&
    visible.every((r) => selected.has(r.key));

  const toggleAllVisible = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visible.forEach((r) => next.delete(r.key));
      } else {
        const seen = new Set();
        visible.forEach((r) => {
          if (r.kind === "suggestion") {
            const k = String(r.itemId);
            if (seen.has(k)) return;
            seen.add(k);
          }
          next.add(r.key);
        });
      }
      return next;
    });

  // Everything ticked, across all tabs
  const selectedRows = useMemo(() => {
    if (!result) return [];
    const fromSku = liveRows.filter(
      (r) => selected.has(r.key) && r.newPrice !== null,
    );
    // never write the same item twice – keep the most confident suggestion
    const best = new Map();
    liveSuggestions
      .filter((s) => selected.has(s.key) && s.newPrice !== null)
      .forEach((s) => {
        const k = String(s.itemId);
        if (!best.has(k) || s.confidence > best.get(k).confidence)
          best.set(k, s);
      });
    return [...fromSku, ...best.values()];
  }, [result, liveRows, liveSuggestions, selected]);

  // Ticked AND currently visible (current tab + filters + search).
  // This is what the main Apply button saves.
  const scopedRows = useMemo(() => {
    if (!selectableTab) return [];
    const keys = new Set(visible.map((r) => r.key));
    return selectedRows.filter((r) => keys.has(r.key));
  }, [selectableTab, visible, selectedRows]);

  const tickedElsewhere = selectedRows.length - scopedRows.length;

  const scopedMismatches = scopedRows.filter(
    (r) => r.status === "mismatch" || r.unitOk === false,
  ).length;
  const scopedSuggestions = scopedRows.filter(
    (r) => r.kind === "suggestion",
  ).length;
  const scopedLowConfidence = scopedRows.filter(
    (r) => r.kind === "suggestion" && !isAutoSelectable(r),
  ).length;

  // ─── Find similar items (local similarity → Groq picks) ───────────────────
  const findSimilar = async () => {
    if (!result || ai.running) return;

    // vendor lines / items already saved from an earlier run are left alone
    const appliedSugs = suggestions.filter((s) => applied.has(s.key));
    const appliedSkus = new Set(appliedSugs.map((s) => normSku(s.sku)));
    const unmatched = result.unmatchedVendor.filter(
      (v) => !appliedSkus.has(normSku(v.sku)),
    );
    if (!unmatched.length) return;

    const matchedIds = new Set([
      ...result.rows.map((r) => String(r.itemId)),
      ...appliedSugs.map((s) => String(s.itemId)),
    ]);
    const preferIds = new Set(result.missingFromFile.map((m) => String(m.key)));
    const cands = suggestCandidates({
      unmatched,
      catalog: catalogRef.current,
      excludeItemIds: matchedIds,
      preferItemIds: preferIds,
    });

    // clear any previous run's picks
    setSelected((prev) => {
      const next = new Set(prev);
      [...next]
        .filter((k) => k.startsWith("s:"))
        .forEach((k) => next.delete(k));
      return next;
    });

    if (!cands.length) {
      setSuggestions(appliedSugs);
      setAi({
        running: false,
        done: 0,
        total: 0,
        ran: true,
        warning: "No items with a similar description were found.",
      });
      return;
    }

    setAi({
      running: true,
      done: 0,
      total: cands.length,
      ran: false,
      warning: "",
    });
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    const aiPick = new Map(); // candidate-list index -> { itemId, confidence, reason }
    let aiFailed = 0;
    let aiDown = false;

    for (let b = 0; b < cands.length; b += AI_BATCH) {
      const slice = cands.slice(b, b + AI_BATCH);

      if (aiDown) {
        aiFailed += slice.length;
        continue;
      }

      const rows = slice.map((c, i) => ({
        idx: b + i,
        sku: c.vendorRow.sku,
        description: c.vendorRow.description,
        unit: c.vendorRow.unit,
        candidates: c.candidates.map((x) => ({
          id: String(x.item._id),
          code: x.item.itemCode,
          description: x.item.description,
          size: x.item.size || "",
          uom: x.item.uom || "",
        })),
      }));

      let ok = false;
      for (let attempt = 0; attempt < 3 && !ok; attempt++) {
        try {
          const res = await axios.post(
            `${API_BASE}/item-database/ai-match`,
            { rows },
            { headers },
          );
          (res.data.results || []).forEach((r) => aiPick.set(r.idx, r));
          ok = true;
        } catch (err) {
          const status = err.response?.status;
          if (status === 429 && attempt < 2) {
            await sleep(4000 * (attempt + 1)); // Groq rate limit → back off
            continue;
          }
          if (status === 503) aiDown = true; // key not configured → stop trying
          break;
        }
      }
      if (!ok) aiFailed += slice.length;
      setAi((p) => ({ ...p, done: Math.min(cands.length, b + slice.length) }));
    }

    // Build suggestion rows
    const out = [];
    cands.forEach((c, i) => {
      const pick = aiPick.get(i);
      if (pick) {
        if (!pick.itemId) return; // AI: none of these is the same product
        const cand = c.candidates.find(
          (x) => String(x.item._id) === String(pick.itemId),
        );
        if (!cand) return;
        out.push(
          buildSuggestionRow({
            vendorRow: c.vendorRow,
            item: cand.item,
            vendorId: vendor.value,
            confidence: pick.confidence,
            reason: pick.reason,
            source: "ai",
            specOk: cand.specOk,
            specNote: cand.specNote,
          }),
        );
      } else {
        // AI unavailable for this line → only keep strong text matches,
        // capped so they are never pre-ticked
        const top = c.candidates[0];
        if (top.score < 0.6) return;
        out.push(
          buildSuggestionRow({
            vendorRow: c.vendorRow,
            item: top.item,
            vendorId: vendor.value,
            confidence: Math.min(70, Math.round(top.score * 100)),
            reason: "Closest text match (AI not used)",
            source: "local",
            specOk: top.specOk,
            specNote: top.specNote,
          }),
        );
      }
    });

    // Same item suggested for two vendor lines → keep the best, flag the rest
    const seen = new Map();
    out
      .sort((a, b) => b.confidence - a.confidence)
      .forEach((r) => {
        const k = String(r.itemId);
        if (seen.has(k)) {
          r.conflict = true;
          r.note = [r.note, `Item is also the best match for ${seen.get(k)}`]
            .filter(Boolean)
            .join(" · ");
        } else {
          seen.set(k, r.sku);
        }
      });

    setSuggestions([...appliedSugs, ...out]);
    setSelected((prev) => {
      const next = new Set(prev);
      out.filter(isAutoSelectable).forEach((r) => next.add(r.key));
      return next;
    });
    setAi({
      running: false,
      done: cands.length,
      total: cands.length,
      ran: true,
      warning: aiDown
        ? "AI isn't configured on the server (GROQ_API_KEY) — showing text-similarity matches only."
        : aiFailed
          ? `AI was unavailable for ${aiFailed} line${aiFailed === 1 ? "" : "s"} — those use text similarity only and are not pre-ticked.`
          : "",
    });
    setTab("suggested");
    setShowAll(false);
  };

  // ─── Apply ─────────────────────────────────────────────────────────────────
  // Saves the given rows, then removes them from the lists so you can carry on
  // with the next tab. Rows the server rejected stay put (and stay ticked).
  const applyRows = async (rowsToApply) => {
    if (!rowsToApply.length || applying || ai.running) return;
    setApplying(true);
    setError("");
    setLastApply(null);

    // Aggregate across chunks; commit state in `finally` so a failure part-way
    // still reflects what was already saved (each chunk is its own transaction).
    const agg = {
      updated: 0,
      unchanged: 0,
      skuChanged: 0,
      vendorEntriesAdded: 0,
      errors: [],
    };
    const okKeys = [];
    const newLogIds = [];
    let failure = "";
    try {
      const token = localStorage.getItem("token");
      for (let i = 0; i < rowsToApply.length; i += APPLY_CHUNK) {
        const chunk = rowsToApply.slice(i, i + APPLY_CHUNK);
        let d;
        try {
          const res = await axios.post(
            `${API_BASE}/item-database/price-update`,
            {
              vendorId: vendor.value,
              fileName: file?.name || "",
              sessionId: sessionIdRef.current,
              updates: chunk.map((r) => ({
                itemId: r.itemId,
                price: r.newPrice,
                // description matches also bring the vendor's SKU across
                ...(r.kind === "suggestion"
                  ? { partNumber: r.sku, addVendorEntry: !r.hasVendorEntry }
                  : {}),
                meta: {
                  vendorSku: r.sku || "",
                  vendorPrice: r.vendorPrice ?? null,
                  vendorUnit: r.vendorUnit || "",
                  vendorDescription: r.vendorDescription || "",
                  matchType:
                    r.kind === "suggestion"
                      ? r.source === "ai"
                        ? "ai"
                        : "text"
                      : "sku",
                  confidence: r.kind === "suggestion" ? r.confidence : 100,
                  reason:
                    r.reason || (r.converted || r.usedAlt ? r.note || "" : ""),
                },
              })),
            },
            { headers: { Authorization: `Bearer ${token}` } },
          );
          d = res.data;
        } catch (err) {
          failure =
            (err.response?.data?.message || err.message) +
            (okKeys.length
              ? ` (${okKeys.length} already saved before this error)`
              : "");
          break;
        }
        const errs = d.errors || [];
        const failedIds = new Set(
          errs.map((e) => String(e.itemId || "")).filter(Boolean),
        );
        chunk
          .filter((r) => !failedIds.has(String(r.itemId)))
          .forEach((r) => okKeys.push(r.key));
        agg.updated += d.updated || 0;
        agg.unchanged += d.unchanged || 0;
        agg.skuChanged += d.skuChanged || 0;
        agg.vendorEntriesAdded += d.vendorEntriesAdded || 0;
        agg.errors.push(...errs);
        if (d.logId) newLogIds.push(d.logId);
      }
    } finally {
      if (okKeys.length || agg.errors.length) {
        setApplied((prev) => new Set([...prev, ...okKeys]));
        setSelected((prev) => {
          const next = new Set(prev);
          okKeys.forEach((k) => next.delete(k));
          return next;
        });
        setTotals((t) => ({
          updated: t.updated + agg.updated,
          unchanged: t.unchanged + agg.unchanged,
          skuChanged: t.skuChanged + agg.skuChanged,
          vendorEntriesAdded: t.vendorEntriesAdded + agg.vendorEntriesAdded,
          errors: [...t.errors, ...agg.errors],
        }));
        setLastApply({ ...agg });
        if (newLogIds.length) setLogIds((p) => [...p, ...newLogIds]);
        onUpdated?.();
      }
      if (failure) setError(failure);
      setApplying(false);
    }
  };

  // ─── Export what was applied in this session ───────────────────────────────
  const exportApplied = async (kind) => {
    if (!logIds.length || exporting) return;
    setExporting(true);
    setError("");
    try {
      const logs = await fetchPriceUpdateLogs(logIds);
      kind === "csv"
        ? downloadPriceUpdateCsv(logs)
        : downloadPriceUpdateXlsx(logs);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setExporting(false);
    }
  };

  const applyHere = () => applyRows(scopedRows);
  const applyAllTicked = () => applyRows(selectedRows);

  const finish = () => {
    setApplyResult({ ...totals, vendor: vendor?.label });
    setPhase("done");
  };

  const reset = () => {
    setPhase("setup");
    setResult(null);
    setApplyResult(null);
    setApplied(new Set());
    setTotals(EMPTY_TOTALS);
    setLastApply(null);
    setLogIds([]);
    sessionIdRef.current = `pu-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    setConfMin(0);
    setSafeOnly(false);
    setDir("all");
    setSelected(new Set());
    setSuggestions([]);
    setAi({ running: false, done: 0, total: 0, ran: false, warning: "" });
    setFile(null);
    setError("");
  };

  // ─── Render helpers ────────────────────────────────────────────────────────
  const Th = ({ children, right, className }) => (
    <th
      className={cls(
        "px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500 bg-gray-900 sticky top-0 z-10 border-b border-gray-800 whitespace-nowrap",
        right ? "text-right" : "text-left",
        className,
      )}
    >
      {children}
    </th>
  );

  const renderMatchedRow = (r) => {
    const isUp = r.status === "increase";
    const isDown = r.status === "decrease";
    const on = selected.has(r.key);
    return (
      <tr
        key={r.key}
        onClick={() => toggle(r.key)}
        className={cls(
          "border-b border-gray-800/60 cursor-pointer transition-colors",
          on
            ? "bg-orange-500/5 hover:bg-orange-500/10"
            : "hover:bg-gray-800/40",
        )}
      >
        <td className="px-3 py-2 w-8">
          <input
            type="checkbox"
            checked={on}
            onChange={() => toggle(r.key)}
            onClick={(e) => e.stopPropagation()}
            className="accent-orange-500"
          />
        </td>
        <td className="px-3 py-2">
          <div className="font-mono text-[12px] text-orange-400">
            {r.itemCode}
          </div>
          <div className="text-[11px] text-gray-400 max-w-[260px] truncate">
            {r.description}
            {r.size ? ` · ${r.size}` : ""}
          </div>
        </td>
        <td className="px-3 py-2 font-mono text-[12px] text-gray-300">
          {r.sku}
        </td>
        <td className="px-3 py-2 text-right font-mono text-[12px] text-gray-400 whitespace-nowrap">
          {r.vendorPrice.toLocaleString("en-US", {
            maximumFractionDigits: 4,
          })}{" "}
          <span className="text-gray-600">{r.vendorUnit}</span>
        </td>
        <td className="px-3 py-2 text-right font-mono text-[12px] text-gray-200 whitespace-nowrap">
          {fmtPrice(r.newPrice)}
          <span className="text-gray-600 text-[10px]"> /{r.uom}</span>
          {(r.converted || r.usedAlt) && (
            <div
              className="text-[10px] font-sans text-blue-300/80 max-w-[240px] whitespace-normal text-right"
              title={r.note}
            >
              {r.converted ? "Computed" : "Alt price"}
              {r.newMsf !== null && r.newMsf !== undefined
                ? ` · ${fmtPrice(r.newMsf)}/M${r.uom}`
                : ""}
              <div className="text-gray-500">{r.note}</div>
            </div>
          )}
        </td>
        <td className="px-3 py-2 text-right font-mono text-[12px] text-gray-500 whitespace-nowrap">
          {fmtPrice(r.oldPrice)}
        </td>
        <td
          className={cls(
            "px-3 py-2 text-right font-mono text-[12px] whitespace-nowrap",
            isUp && "text-red-400",
            isDown && "text-green-400",
            !isUp && !isDown && "text-gray-500",
          )}
        >
          {r.diff === null ? "—" : fmtDiff(r.diff)}
          <div className="text-[10px] opacity-80">
            {fmtPct(r.pct)}
            {r.bigChange && (
              <ExclamationTriangleIcon
                className="inline h-3 w-3 ml-1 text-yellow-400"
                title="Large change — double-check"
              />
            )}
          </div>
        </td>
        {tab === "review" && (
          <td className="px-3 py-2 text-[10px] text-yellow-500/90 max-w-[220px]">
            {r.note || "Could not read a valid price"}
          </td>
        )}
      </tr>
    );
  };

  const renderSuggestionRow = (r) => {
    const on = selected.has(r.key);
    const isUp = r.diff > 0;
    const isDown = r.diff < 0;
    const confCls =
      r.confidence >= 85
        ? "bg-green-500/15 text-green-300 border-green-500/30"
        : r.confidence >= 70
          ? "bg-yellow-500/15 text-yellow-300 border-yellow-500/30"
          : "bg-red-500/15 text-red-300 border-red-500/30";
    return (
      <tr
        key={r.key}
        onClick={() => toggle(r.key)}
        className={cls(
          "border-b border-gray-800/60 cursor-pointer transition-colors align-top",
          on
            ? "bg-orange-500/5 hover:bg-orange-500/10"
            : "hover:bg-gray-800/40",
        )}
      >
        <td className="px-3 py-2 w-8">
          <input
            type="checkbox"
            checked={on}
            onChange={() => toggle(r.key)}
            onClick={(e) => e.stopPropagation()}
            className="accent-orange-500"
          />
        </td>
        <td className="px-3 py-2 max-w-[230px]">
          <div className="font-mono text-[12px] text-gray-200">{r.sku}</div>
          <div className="text-[11px] text-gray-500 line-clamp-2">
            {r.vendorDescription}
          </div>
          <div className="text-[10px] font-mono text-gray-600 mt-0.5">
            {r.vendorPrice.toLocaleString("en-US", {
              maximumFractionDigits: 4,
            })}{" "}
            {r.vendorUnit}
          </div>
        </td>
        <td className="px-3 py-2 max-w-[230px]">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-mono text-[12px] text-orange-400">
              {r.itemCode}
            </span>
            {!r.hasVendorEntry && (
              <span className="px-1.5 py-0.5 text-[9px] font-semibold rounded border bg-blue-500/15 text-blue-300 border-blue-500/30">
                + NEW VENDOR ENTRY
              </span>
            )}
          </div>
          <div className="text-[11px] text-gray-400 line-clamp-2">
            {r.description}
            {r.size ? ` · ${r.size}` : ""}
          </div>
        </td>
        <td className="px-3 py-2 font-mono text-[11px] whitespace-nowrap">
          <span
            className={
              r.oldSku ? "text-gray-500 line-through" : "text-gray-600"
            }
          >
            {r.oldSku || "none"}
          </span>
          <ArrowRightIcon className="inline h-3 w-3 mx-1 text-gray-600" />
          <span className="text-green-300">{r.sku}</span>
        </td>
        <td className="px-3 py-2 max-w-[240px]">
          <div className="flex items-center gap-1.5">
            <span
              className={cls(
                "px-1.5 py-0.5 text-[10px] font-semibold rounded border",
                confCls,
              )}
            >
              {r.confidence}%
            </span>
            <span className="text-[9px] uppercase tracking-wide text-gray-600">
              {r.source === "ai" ? "AI" : "text only"}
            </span>
          </div>
          {r.reason && (
            <div className="text-[10px] text-gray-500 mt-0.5">{r.reason}</div>
          )}
          {r.note && (
            <div className="text-[10px] text-yellow-500/90 mt-0.5">
              {r.note}
            </div>
          )}
        </td>
        <td className="px-3 py-2 text-right font-mono text-[12px] text-gray-200 whitespace-nowrap">
          {fmtPrice(r.newPrice)}
          <span className="text-gray-600 text-[10px]"> /{r.uom}</span>
        </td>
        <td className="px-3 py-2 text-right font-mono text-[12px] text-gray-500 whitespace-nowrap">
          {r.oldPrice === null ? "—" : fmtPrice(r.oldPrice)}
        </td>
        <td
          className={cls(
            "px-3 py-2 text-right font-mono text-[12px] whitespace-nowrap",
            isUp && "text-red-400",
            isDown && "text-green-400",
            !isUp && !isDown && "text-gray-500",
          )}
        >
          {r.diff === null ? "—" : fmtDiff(r.diff)}
          <div className="text-[10px] opacity-80">
            {fmtPct(r.pct)}
            {r.bigChange && (
              <ExclamationTriangleIcon
                className="inline h-3 w-3 ml-1 text-yellow-400"
                title="Large change — double-check"
              />
            )}
          </div>
        </td>
      </tr>
    );
  };

  const canCompare = !!vendor && !!file && !busy;

  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-1 h-5 bg-orange-500 rounded-full" />
            <div>
              <h3 className="text-sm font-semibold text-white">
                Update Pricing from Vendor Price List
              </h3>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Matches vendor item numbers to the SKUs saved on each item's
                vendor price · MSF / MLF are converted to our per-unit price
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={applying}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-gray-800 disabled:opacity-40"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mx-5 mt-4 px-4 py-2.5 bg-red-900/30 border border-red-800 rounded-lg text-sm text-red-300 shrink-0">
            {error}
          </div>
        )}

        {/* ── Step 1: vendor + file ─────────────────────────────────────── */}
        {phase === "setup" && (
          <div className="p-5 space-y-5 overflow-y-auto">
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
                1 · Vendor
              </label>
              <Select
                options={vendorOptions}
                value={vendor}
                onChange={setVendor}
                placeholder="Select the vendor this price list is from…"
                styles={selectStyles}
                menuPortalTarget={
                  typeof document !== "undefined" ? document.body : null
                }
                isClearable
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
                2 · Vendor price list (Excel)
              </label>
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  pickFile(e.dataTransfer.files?.[0]);
                }}
                onClick={() => fileInputRef.current?.click()}
                className={cls(
                  "flex flex-col items-center justify-center gap-2 py-10 rounded-xl border-2 border-dashed cursor-pointer transition-colors",
                  dragOver
                    ? "border-orange-500 bg-orange-500/5"
                    : file
                      ? "border-green-700/60 bg-green-900/10"
                      : "border-gray-700 hover:border-gray-600 bg-gray-800/30",
                )}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={(e) => pickFile(e.target.files?.[0])}
                />
                {file ? (
                  <>
                    <CheckIcon className="h-6 w-6 text-green-400" />
                    <p className="text-sm text-gray-200">{file.name}</p>
                    <p className="text-[11px] text-gray-500">
                      Click or drop to choose a different file
                    </p>
                  </>
                ) : (
                  <>
                    <ArrowUpTrayIcon className="h-6 w-6 text-gray-500" />
                    <p className="text-sm text-gray-300">
                      Drop the vendor's price list here, or click to browse
                    </p>
                    <p className="text-[11px] text-gray-600">
                      .xlsx · .xls · .csv
                    </p>
                  </>
                )}
              </div>
            </div>

            <div className="text-[11px] text-gray-500 bg-gray-800/40 border border-gray-800 rounded-lg px-4 py-3 space-y-1">
              <p>
                <span className="text-gray-300 font-medium">Matching:</span> the
                vendor's <span className="text-gray-300">Item</span> number is
                matched to the <span className="text-gray-300">Vendor SKU</span>{" "}
                saved on that vendor's price entry. Variant rows are ignored —
                the item's Unit Price is used.
              </p>
              <p>
                <span className="text-gray-300 font-medium">Units:</span> 535
                MSF → $0.535 per SF/LF · 366 MLF → $0.366 per LF. Anything where
                the units don't line up is flagged for review instead of being
                applied.
              </p>
              <p>
                Nothing is saved until you review the comparison and click
                Apply.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={runCompare}
                disabled={!canCompare}
                className="px-5 py-2 text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg flex items-center gap-2"
              >
                {busy && (
                  <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                )}
                {busy ? "Comparing…" : "Compare prices"}
              </button>
            </div>
          </div>
        )}

        {/* ── Step 2: review ────────────────────────────────────────────── */}
        {phase === "review" && result && (
          <>
            {/* summary strip */}
            <div className="px-5 pt-4 pb-3 shrink-0 flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px] text-gray-500">
              <span>
                <span className="text-gray-200 font-semibold">
                  {vendor?.label}
                </span>{" "}
                · {file?.name}
              </span>
              <span>
                <span className="text-gray-200 font-semibold">
                  {result.summary.vendorRows}
                </span>{" "}
                vendor items read
              </span>
              <span>
                <span className="text-gray-200 font-semibold">
                  {result.summary.matched}
                </span>{" "}
                matched by SKU
              </span>
              <span>
                <span className="text-red-400 font-semibold">
                  {tabCounts.up}
                </span>{" "}
                up ·{" "}
                <span className="text-green-400 font-semibold">
                  {tabCounts.down}
                </span>{" "}
                down ·{" "}
                <span className="text-gray-300 font-semibold">
                  {tabCounts.same}
                </span>{" "}
                same
                <span className="text-gray-600"> (still to apply)</span>
              </span>
              {totals.updated > 0 && (
                <span className="text-green-400">
                  <CheckIcon className="inline h-3.5 w-3.5 -mt-0.5" />{" "}
                  <span className="font-semibold">{totals.updated}</span>{" "}
                  applied so far
                  {totals.skuChanged > 0 && ` · ${totals.skuChanged} SKUs`}
                </span>
              )}
              {result.summary.ourWithoutSku > 0 && (
                <span className="text-yellow-500">
                  {result.summary.ourWithoutSku} of our {vendor?.label} items
                  have no SKU saved
                </span>
              )}
              {parseInfo?.duplicateSkus > 0 && (
                <span className="text-yellow-500">
                  {parseInfo.duplicateSkus} duplicate item numbers in file (last
                  one used)
                </span>
              )}
            </div>

            {/* tabs + search */}
            <div className="px-5 pb-3 shrink-0 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-1 bg-gray-800/60 border border-gray-700 rounded-xl p-1 flex-wrap">
                {TABS.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => {
                      setTab(t.id);
                      setShowAll(false);
                    }}
                    className={cls(
                      "px-3 py-1.5 text-[11px] font-semibold rounded-lg flex items-center gap-1.5 transition-colors",
                      tab === t.id
                        ? "bg-orange-500 text-white"
                        : "text-gray-400 hover:text-white hover:bg-gray-700/60",
                    )}
                  >
                    {t.label}
                    <span
                      className={cls(
                        "text-[9px] px-1.5 py-0.5 rounded-full font-mono",
                        tab === t.id
                          ? "bg-black/20"
                          : "bg-gray-700 text-gray-400",
                      )}
                    >
                      {tabCounts[t.id] ?? 0}
                    </span>
                  </button>
                ))}
              </div>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search SKU, code, description…"
                  className="pl-8 pr-3 py-1.5 w-64 text-sm bg-gray-950 border border-gray-800 rounded-lg text-gray-200 placeholder-gray-600 focus:border-orange-500 focus:outline-none"
                />
              </div>
            </div>

            {/* find-similar banner */}
            {(tab === "notInDb" || tab === "suggested") &&
              result.summary.unmatchedVendor > 0 && (
                <div className="mx-5 mb-3 px-4 py-3 rounded-xl border border-orange-500/30 bg-orange-500/5 shrink-0">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-start gap-2.5 min-w-0">
                      <SparklesIcon className="h-4 w-4 text-orange-400 mt-0.5 shrink-0" />
                      <p className="text-[12px] text-gray-300">
                        <span className="font-semibold text-white">
                          {result.summary.unmatchedVendor}
                        </span>{" "}
                        vendor items have no SKU match. Find items with a
                        similar description to attach the vendor's SKU and
                        update the price — useful when the vendor renumbers an
                        item or a SKU was never saved.
                      </p>
                    </div>
                    <button
                      onClick={findSimilar}
                      disabled={ai.running}
                      className="px-4 py-1.5 text-xs font-semibold text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-60 rounded-lg flex items-center gap-2 shrink-0"
                    >
                      {ai.running ? (
                        <>
                          <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                          Matching {ai.done}/{ai.total}…
                        </>
                      ) : ai.ran ? (
                        "Run again"
                      ) : (
                        "Find similar items"
                      )}
                    </button>
                  </div>
                  {ai.running && ai.total > 0 && (
                    <div className="mt-2 h-1 bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-orange-500 transition-all"
                        style={{ width: `${(ai.done / ai.total) * 100}%` }}
                      />
                    </div>
                  )}
                  {ai.warning && (
                    <p className="mt-2 text-[11px] text-yellow-500">
                      {ai.warning}
                    </p>
                  )}
                  {ai.ran && !ai.running && (
                    <p className="mt-2 text-[11px] text-gray-500">
                      {suggestions.length} suggested match
                      {suggestions.length === 1 ? "" : "es"}. Only matches the
                      AI is ≥ {AUTO_SELECT_CONFIDENCE}% sure about (with sizes,
                      gauge and units agreeing) are pre-ticked — review the rest
                      before applying.
                    </p>
                  )}
                </div>
              )}

            {/* filters + tick-all (selectable tabs only) */}
            {selectableTab && (
              <div className="mx-5 mb-3 shrink-0 flex flex-wrap items-center gap-x-4 gap-y-2">
                {tab === "suggested" && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] uppercase tracking-wider text-gray-600">
                      Confidence
                    </span>
                    <div className="flex items-center gap-0.5 bg-gray-800/60 border border-gray-700 rounded-lg p-0.5">
                      {CONF_STEPS.map((c) => (
                        <button
                          key={c.value}
                          onClick={() => {
                            setConfMin(c.value);
                            setShowAll(false);
                          }}
                          className={cls(
                            "px-2 py-1 text-[11px] font-semibold rounded-md transition-colors",
                            confMin === c.value
                              ? "bg-orange-500 text-white"
                              : "text-gray-400 hover:text-white hover:bg-gray-700/60",
                          )}
                        >
                          {c.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {(tab === "changes" || tab === "suggested") && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] uppercase tracking-wider text-gray-600">
                      Change
                    </span>
                    <div className="flex items-center gap-0.5 bg-gray-800/60 border border-gray-700 rounded-lg p-0.5">
                      {DIR_STEPS.map((c) => (
                        <button
                          key={c.value}
                          onClick={() => {
                            setDir(c.value);
                            setShowAll(false);
                          }}
                          className={cls(
                            "px-2 py-1 text-[11px] font-semibold rounded-md transition-colors",
                            dir === c.value
                              ? "bg-orange-500 text-white"
                              : "text-gray-400 hover:text-white hover:bg-gray-700/60",
                          )}
                        >
                          {c.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {tab === "suggested" && (
                  <label className="flex items-center gap-1.5 text-[11px] text-gray-400 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={safeOnly}
                      onChange={(e) => {
                        setSafeOnly(e.target.checked);
                        setShowAll(false);
                      }}
                      className="accent-orange-500"
                    />
                    Only where sizes &amp; units agree
                  </label>
                )}

                <div className="ml-auto flex items-center gap-3">
                  <span className="text-[11px] text-gray-500">
                    <span className="text-gray-200 font-semibold">
                      {visible.length}
                    </span>{" "}
                    shown
                  </span>
                  <button
                    onClick={toggleAllVisible}
                    disabled={!visible.length}
                    className="px-3 py-1 text-[11px] font-semibold text-orange-300 hover:text-orange-200 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 rounded-lg disabled:opacity-40"
                  >
                    {allVisibleSelected
                      ? "Untick all shown"
                      : `Tick all shown (${visible.length})`}
                  </button>
                </div>
              </div>
            )}

            {/* table */}
            <div className="flex-1 overflow-auto border-t border-gray-800 min-h-[200px]">
              {visible.length === 0 ? (
                <div className="py-16 text-center text-sm text-gray-500">
                  Nothing to show here.
                </div>
              ) : (
                <table className="w-full text-sm border-collapse">
                  <thead>
                    {(tab === "changes" ||
                      tab === "review" ||
                      tab === "same") && (
                      <tr>
                        <Th className="w-8">
                          {selectableTab && (
                            <input
                              type="checkbox"
                              checked={allVisibleSelected}
                              onChange={toggleAllVisible}
                              className="accent-orange-500"
                            />
                          )}
                        </Th>
                        <Th>Our item</Th>
                        <Th>Vendor SKU</Th>
                        <Th right>Vendor price</Th>
                        <Th right>New (our unit)</Th>
                        <Th right>Current</Th>
                        <Th right>Change</Th>
                        {tab === "review" && <Th>Why</Th>}
                      </tr>
                    )}
                    {tab === "suggested" && (
                      <tr>
                        <Th className="w-8">
                          <input
                            type="checkbox"
                            checked={allVisibleSelected}
                            onChange={toggleAllVisible}
                            className="accent-orange-500"
                          />
                        </Th>
                        <Th>Vendor line</Th>
                        <Th>Our item</Th>
                        <Th>SKU</Th>
                        <Th>Match</Th>
                        <Th right>New (our unit)</Th>
                        <Th right>Current</Th>
                        <Th right>Change</Th>
                      </tr>
                    )}
                    {tab === "notInDb" && (
                      <tr>
                        <Th>Vendor item #</Th>
                        <Th>Description</Th>
                        <Th right>Unit price</Th>
                      </tr>
                    )}
                    {tab === "notInFile" && (
                      <tr>
                        <Th>Our item</Th>
                        <Th>Description</Th>
                        <Th>Saved SKU</Th>
                        <Th right>Current</Th>
                      </tr>
                    )}
                  </thead>
                  <tbody>
                    {(tab === "changes" || tab === "review") &&
                      shown.map(renderMatchedRow)}

                    {tab === "suggested" && shown.map(renderSuggestionRow)}

                    {tab === "same" &&
                      shown.map((r) => (
                        <tr key={r.key} className="border-b border-gray-800/60">
                          <td />
                          <td className="px-3 py-2">
                            <div className="font-mono text-[12px] text-orange-400">
                              {r.itemCode}
                            </div>
                            <div className="text-[11px] text-gray-400 max-w-[260px] truncate">
                              {r.description}
                            </div>
                          </td>
                          <td className="px-3 py-2 font-mono text-[12px] text-gray-300">
                            {r.sku}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-[12px] text-gray-400">
                            {r.vendorPrice} {r.vendorUnit}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-[12px] text-gray-200">
                            {fmtPrice(r.newPrice)}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-[12px] text-gray-500">
                            {fmtPrice(r.oldPrice)}
                          </td>
                          <td className="px-3 py-2 text-right text-[11px] text-gray-600">
                            same
                          </td>
                        </tr>
                      ))}

                    {tab === "notInDb" &&
                      shown.map((v) => (
                        <tr
                          key={`${v.sku}-${v.row}`}
                          className="border-b border-gray-800/60"
                        >
                          <td className="px-3 py-2 font-mono text-[12px] text-gray-300">
                            {v.sku}
                          </td>
                          <td className="px-3 py-2 text-[11px] text-gray-400">
                            {v.description}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-[12px] text-gray-400">
                            {v.unitPrice.toLocaleString("en-US", {
                              maximumFractionDigits: 4,
                            })}{" "}
                            <span className="text-gray-600">{v.unit}</span>
                          </td>
                        </tr>
                      ))}

                    {tab === "notInFile" &&
                      shown.map((m) => (
                        <tr key={m.key} className="border-b border-gray-800/60">
                          <td className="px-3 py-2 font-mono text-[12px] text-orange-400">
                            {m.itemCode}
                          </td>
                          <td className="px-3 py-2 text-[11px] text-gray-400">
                            {m.description}
                          </td>
                          <td className="px-3 py-2 font-mono text-[12px] text-gray-300">
                            {m.sku}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-[12px] text-gray-500">
                            {fmtPrice(m.oldPrice)}/{m.uom}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              )}
              {!showAll && visible.length > ROW_LIMIT && (
                <div className="py-3 text-center">
                  <button
                    onClick={() => setShowAll(true)}
                    className="text-xs text-orange-400 hover:text-orange-300 underline underline-offset-2"
                  >
                    Showing {ROW_LIMIT} of {visible.length} — show all
                  </button>
                </div>
              )}
            </div>

            {/* footer */}
            <div className="px-5 py-3 border-t border-gray-800 shrink-0 flex flex-wrap items-center justify-between gap-3">
              <div className="text-[11px] text-gray-500 min-w-0 space-y-0.5">
                {selectableTab ? (
                  <div>
                    <span
                      data-testid="scoped-count"
                      className="text-gray-200 font-semibold"
                    >
                      {scopedRows.length}
                    </span>{" "}
                    ticked in this view
                    {scopedSuggestions > 0 && (
                      <span className="text-gray-400">
                        {" "}
                        · {scopedSuggestions} will also update the SKU
                      </span>
                    )}
                    {scopedLowConfidence > 0 && (
                      <span className="text-yellow-500">
                        {" "}
                        · {scopedLowConfidence} below auto-tick confidence
                      </span>
                    )}
                    {scopedMismatches > 0 && (
                      <span className="text-yellow-500">
                        {" "}
                        · {scopedMismatches} with unit mismatch
                      </span>
                    )}
                    {tickedElsewhere > 0 && (
                      <span className="text-gray-500">
                        {" "}
                        · {tickedElsewhere} more ticked in other tabs / hidden
                        by filters
                      </span>
                    )}
                  </div>
                ) : (
                  <div>
                    Nothing to apply on this tab — switch to Price changes,
                    Suggested matches or Needs review.
                    {selectedRows.length > 0 && (
                      <span> {selectedRows.length} ticked elsewhere.</span>
                    )}
                  </div>
                )}
                {lastApply && (
                  <div className="text-green-400">
                    <CheckIcon className="inline h-3.5 w-3.5 -mt-0.5" /> Saved{" "}
                    {lastApply.updated}
                    {lastApply.skuChanged > 0 &&
                      ` · ${lastApply.skuChanged} SKU${lastApply.skuChanged === 1 ? "" : "s"} updated`}
                    {lastApply.vendorEntriesAdded > 0 &&
                      ` · ${lastApply.vendorEntriesAdded} vendor entr${lastApply.vendorEntriesAdded === 1 ? "y" : "ies"} added`}
                    {lastApply.unchanged > 0 &&
                      ` · ${lastApply.unchanged} already up to date`}
                    {lastApply.errors.length > 0 && (
                      <span className="text-red-400">
                        {" "}
                        · {lastApply.errors.length} failed (left in the list):{" "}
                        {lastApply.errors
                          .slice(0, 3)
                          .map((e) => `${e.itemCode || e.itemId}: ${e.error}`)
                          .join("; ")}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <button
                  onClick={reset}
                  disabled={applying}
                  className="px-3 py-2 text-sm text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg disabled:opacity-40"
                >
                  Start over
                </button>
                {logIds.length > 0 && (
                  <span className="flex items-center gap-1">
                    <button
                      onClick={() => exportApplied("xlsx")}
                      disabled={exporting || applying}
                      data-testid="export-applied"
                      className="px-3 py-2 text-sm text-orange-300 hover:text-orange-200 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 rounded-lg disabled:opacity-40"
                    >
                      {exporting ? "Preparing…" : "Export applied (Excel)"}
                    </button>
                    <button
                      onClick={() => exportApplied("csv")}
                      disabled={exporting || applying}
                      className="px-2.5 py-2 text-sm text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg disabled:opacity-40"
                    >
                      CSV
                    </button>
                  </span>
                )}
                {(totals.updated > 0 || totals.errors.length > 0) && (
                  <button
                    onClick={finish}
                    disabled={applying}
                    className="px-3 py-2 text-sm text-gray-300 hover:text-white bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg disabled:opacity-40"
                  >
                    Finish
                  </button>
                )}
                {selectedRows.length > 0 &&
                  (!selectableTab ||
                    selectedRows.length !== scopedRows.length) && (
                    <button
                      onClick={applyAllTicked}
                      disabled={applying || ai.running}
                      className="px-3 py-2 text-sm font-semibold text-orange-300 hover:text-orange-200 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 rounded-lg disabled:opacity-40"
                    >
                      Apply all {selectedRows.length} ticked
                    </button>
                  )}
                {selectableTab && (
                  <button
                    onClick={applyHere}
                    data-testid="apply-here"
                    disabled={!scopedRows.length || applying || ai.running}
                    className="px-5 py-2 text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg flex items-center gap-2"
                  >
                    {applying && (
                      <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    )}
                    {applying
                      ? "Applying…"
                      : `Apply ${scopedRows.length} ${APPLY_NOUN[tab][scopedRows.length === 1 ? 0 : 1]}`}
                  </button>
                )}
              </div>
            </div>
          </>
        )}

        {/* ── Step 3: done ──────────────────────────────────────────────── */}
        {phase === "done" && applyResult && (
          <div className="p-6 space-y-4 overflow-y-auto">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-900/30 border border-green-700/50 flex items-center justify-center">
                <CheckIcon className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">
                  {applyResult.updated} price
                  {applyResult.updated === 1 ? "" : "s"} updated for{" "}
                  {applyResult.vendor}
                </p>
                <p className="text-[11px] text-gray-500">
                  {applyResult.skuChanged > 0 &&
                    `${applyResult.skuChanged} SKU${applyResult.skuChanged === 1 ? "" : "s"} updated · `}
                  {applyResult.vendorEntriesAdded > 0 &&
                    `${applyResult.vendorEntriesAdded} vendor entr${applyResult.vendorEntriesAdded === 1 ? "y" : "ies"} added · `}
                  {applyResult.unchanged > 0 &&
                    `${applyResult.unchanged} already up to date · `}
                  {applyResult.errors?.length || 0} error
                  {applyResult.errors?.length === 1 ? "" : "s"}
                </p>
              </div>
            </div>

            {applyResult.errors?.length > 0 && (
              <div className="bg-red-900/20 border border-red-900/50 rounded-lg p-3 max-h-48 overflow-y-auto">
                {applyResult.errors.map((e, i) => (
                  <p key={i} className="text-[11px] text-red-300">
                    <span className="font-mono">{e.itemCode || e.itemId}</span>{" "}
                    — {e.error}
                  </p>
                ))}
              </div>
            )}

            {logIds.length > 0 && (
              <div className="bg-gray-950 border border-gray-800 rounded-lg p-3 flex items-center justify-between gap-3">
                <p className="text-[11px] text-gray-400">
                  Saved to Price History. Export the full before/after list for
                  your records or to share with estimators.
                </p>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => exportApplied("xlsx")}
                    disabled={exporting}
                    data-testid="export-applied-done"
                    className="px-3 py-1.5 text-xs font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-lg disabled:opacity-40"
                  >
                    {exporting ? "Preparing…" : "Export Excel"}
                  </button>
                  <button
                    onClick={() => exportApplied("csv")}
                    disabled={exporting}
                    className="px-3 py-1.5 text-xs text-gray-300 hover:text-white bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg disabled:opacity-40"
                  >
                    CSV
                  </button>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={reset}
                className="px-4 py-2 text-sm text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg"
              >
                Update another vendor
              </button>
              <button
                onClick={onClose}
                className="px-5 py-2 text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-lg"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
